import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, Heart, Phone, User, Pill, 
  Hospital, QrCode, Download, Plus, Trash2,
  CheckCircle, ArrowLeft, Share2,
  CreditCard, Loader2, Lock, Palette
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const RELATIONSHIPS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Doctor', 'Other'];

// Color themes for the emergency card
const COLOR_THEMES = [
  { 
    id: 'red', 
    name: 'Classic Red', 
    primary: 'from-[#D92B2B] to-[#C02424]',
    accent: 'bg-[#D92B2B]',
    badge: 'bg-red-100 text-red-800',
    preview: 'bg-gradient-to-br from-[#D92B2B] to-[#C02424]'
  },
  { 
    id: 'black', 
    name: 'Sleek Black', 
    primary: 'from-slate-800 to-slate-900',
    accent: 'bg-slate-800',
    badge: 'bg-slate-200 text-slate-800',
    preview: 'bg-gradient-to-br from-slate-800 to-slate-900'
  },
  { 
    id: 'blue', 
    name: 'Ocean Blue', 
    primary: 'from-blue-600 to-blue-800',
    accent: 'bg-blue-600',
    badge: 'bg-blue-100 text-blue-800',
    preview: 'bg-gradient-to-br from-blue-600 to-blue-800'
  },
  { 
    id: 'green', 
    name: 'Forest Green', 
    primary: 'from-emerald-600 to-emerald-800',
    accent: 'bg-emerald-600',
    badge: 'bg-emerald-100 text-emerald-800',
    preview: 'bg-gradient-to-br from-emerald-600 to-emerald-800'
  }
];

// Comprehensive lists for autocomplete
const COMMON_CONDITIONS = [
  'Diabetes Type 1', 'Diabetes Type 2', 'Heart Disease', 'Hypertension', 'High Blood Pressure',
  'Asthma', 'COPD', 'Epilepsy', 'Seizure Disorder', 'Cancer', 'Kidney Disease', 'Liver Disease',
  'Alzheimer\'s', 'Dementia', 'Parkinson\'s', 'Multiple Sclerosis', 'Arthritis', 'Osteoporosis',
  'HIV/AIDS', 'Hepatitis B', 'Hepatitis C', 'Stroke', 'Heart Attack History', 'Pacemaker',
  'Blood Clot Disorder', 'Bleeding Disorder', 'Hemophilia', 'Sickle Cell', 'Thyroid Disease',
  'Hypothyroidism', 'Hyperthyroidism', 'Adrenal Insufficiency', 'Depression', 'Anxiety',
  'Bipolar Disorder', 'Schizophrenia', 'PTSD', 'Autism', 'Cerebral Palsy', 'Down Syndrome'
];

// Location-based medication lists
const MEDICATIONS_GENERIC = [
  'Metformin', 'Lisinopril', 'Amlodipine', 'Metoprolol', 'Atorvastatin', 'Omeprazole',
  'Losartan', 'Gabapentin', 'Sertraline', 'Levothyroxine', 'Azithromycin', 'Amoxicillin',
  'Pantoprazole', 'Furosemide', 'Clopidogrel', 'Warfarin', 'Insulin', 'Acetaminophen',
  'Ibuprofen', 'Aspirin', 'Cetirizine', 'Montelukast'
];

const MEDICATIONS_INDIA = [
  // Pain/Fever
  'Crocin', 'Dolo 650', 'Combiflam', 'Saridon', 'Meftal Spas', 'Voveran',
  // Gastric
  'Pan 40', 'Pantocid', 'Rantac', 'Digene', 'Gelusil', 'Mucaine',
  // Diabetes
  'Glycomet', 'Gluconorm', 'Janumet', 'Galvus Met',
  // BP/Heart
  'Telma', 'Amlong', 'Ecosprin', 'Rosulip', 'Atorva',
  // Antibiotics
  'Azithral', 'Azee', 'Monocef', 'Augmentin', 'Cifran',
  // Thyroid/Vitamins
  'Thyronorm', 'Eltroxin', 'Shelcal', 'Becosules', 'Zincovit', 'Revital',
  // Other
  'Allegra', 'Montair', 'Asthalin', 'Ondem',
  // Ayurvedic
  'Ashwagandha', 'Triphala', 'Chyawanprash', 'Liv 52'
];

const MEDICATIONS_US = [
  'Tylenol', 'Advil', 'Motrin', 'Aleve', 'Lipitor', 'Zocor', 'Prilosec', 'Nexium',
  'Synthroid', 'Zoloft', 'Prozac', 'Lexapro', 'Xanax', 'Ambien', 'Norvasc',
  'Toprol', 'Crestor', 'Plavix', 'Eliquis', 'Xarelto', 'Januvia', 'Trulicity',
  'Ozempic', 'Humira', 'Enbrel', 'Ventolin', 'ProAir', 'Symbicort', 'Spiriva'
];

const ALLERGIES_GENERIC = [
  'Penicillin', 'Amoxicillin', 'Aspirin', 'Ibuprofen', 'Sulfa drugs', 'NSAIDs',
  'Codeine', 'Morphine', 'Latex', 'Contrast dye', 'Lidocaine',
  'Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Eggs', 'Milk', 'Soy', 'Wheat', 'Gluten',
  'Bee stings', 'Dust mites', 'Pollen'
];

const ALLERGIES_INDIA = [
  ...ALLERGIES_GENERIC,
  'Brinjal (Eggplant)', 'Chickpeas', 'Lentils (Dal)', 'Mustard oil', 'Cashews',
  'Prawns', 'Coconut', 'Turmeric'
];

const ALLERGIES_US = [
  ...ALLERGIES_GENERIC,
  'Sesame', 'Crab', 'Lobster', 'Shrimp', 'Cashews', 'Almonds', 'Walnuts', 'Pistachios'
];

// Autocomplete Input Component
const AutocompleteInput = ({ value, onChange, onAdd, suggestions, placeholder }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value.length >= 3) {
      const filtered = suggestions.filter(s => 
        s.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 6);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [value, suggestions]);

  const handleSelect = (suggestion) => {
    onAdd(suggestion);
    onChange('');
    setShowSuggestions(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (value.trim()) {
        onAdd(value.trim());
        onChange('');
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => value.length >= 3 && setShowSuggestions(filteredSuggestions.length > 0)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button 
          type="button" 
          onClick={() => { if (value.trim()) { onAdd(value.trim()); onChange(''); }}}
          size="icon"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 border-b border-slate-100 last:border-0"
              onMouseDown={() => handleSelect(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateEmergencyCardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [pendingAllergy, setPendingAllergy] = useState(null); // For severity selection
  
  // Location-based suggestions
  const [userCountry, setUserCountry] = useState('US'); // Default to US
  const [medicationSuggestions, setMedicationSuggestions] = useState([...MEDICATIONS_GENERIC, ...MEDICATIONS_US]);
  const [allergySuggestions, setAllergySuggestions] = useState(ALLERGIES_US);
  
  // Payment state
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [showPaymentBanner, setShowPaymentBanner] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    blood_group: '',
    gender: '',
    emergency_contacts: [{ name: '', relationship: '', phone: '', is_primary: true }],
    allergies: [], // Now stores {name, severity} objects
    critical_conditions: [],
    current_medications: [],
    previous_surgeries: [], // Stores {name, year} objects
    organ_donor: false,
    preferred_hospital: '',
    doctor_name: '',
    doctor_phone: '',
    special_instructions: '',
    color_theme: 'red' // Default theme
  });
  
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newMedication, setNewMedication] = useState('');
  const [newSurgery, setNewSurgery] = useState('');
  const [newSurgeryYear, setNewSurgeryYear] = useState('');

  // Detect user's country for location-based suggestions
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const country = data.country_code || 'US';
        setUserCountry(country);
        
        // Set location-based suggestions
        if (country === 'IN') {
          setMedicationSuggestions([...MEDICATIONS_GENERIC, ...MEDICATIONS_INDIA]);
          setAllergySuggestions(ALLERGIES_INDIA);
        } else if (country === 'US' || country === 'CA') {
          setMedicationSuggestions([...MEDICATIONS_GENERIC, ...MEDICATIONS_US]);
          setAllergySuggestions(ALLERGIES_US);
        } else {
          // Default to generic for other countries
          setMedicationSuggestions(MEDICATIONS_GENERIC);
          setAllergySuggestions(ALLERGIES_GENERIC);
        }
      } catch (error) {
        console.log('Could not detect country, using defaults');
      }
    };
    detectCountry();
  }, []);

  // Check for payment success on page load
  useEffect(() => {
    // Set page title
    document.title = 'Create Your Card - My EmergencyCard App';
    
    const paymentSuccess = searchParams.get('payment_success');
    const sid = searchParams.get('session_id');
    const editData = searchParams.get('edit');
    
    // Handle edit mode - pre-fill form with existing data
    if (editData) {
      try {
        const jsonStr = decodeURIComponent(escape(atob(editData)));
        const data = JSON.parse(jsonStr);
        
        // Expand compressed data to form format
        setFormData({
          full_name: data.n || '',
          date_of_birth: data.d || '',
          blood_group: data.b || '',
          gender: data.g || '',
          allergies: (data.a || []).map(a => ({
            name: a.n,
            severity: a.s === 's' ? 'severe' : a.s === 'm' ? 'moderate' : 'mild'
          })),
          critical_conditions: data.c || [],
          current_medications: data.m || [],
          previous_surgeries: (data.su || []).map(s => ({
            name: s.n,
            year: s.y
          })),
          emergency_contacts: (data.e || []).length > 0 
            ? data.e.map(c => ({
                name: c.n || '',
                relationship: c.r || '',
                phone: c.p || '',
                is_primary: c.i === 1
              }))
            : [{ name: '', relationship: '', phone: '', is_primary: true }],
          organ_donor: data.o === 1,
          preferred_hospital: data.h || '',
          doctor_name: data.dn || '',
          doctor_phone: data.dp || '',
          special_instructions: data.s || '',
          color_theme: data.th || 'red' // Theme
        });
        
        // In edit mode, skip payment verification
        setPaymentVerified(true);
        toast.success('Editing your card - make changes and save!');
      } catch (err) {
        console.error('Error parsing edit data:', err);
        toast.error('Could not load card data for editing');
      }
    }
    
    if (paymentSuccess === 'true' && sid) {
      setSessionId(sid);
      setCheckingPayment(true);
      pollPaymentStatus(sid);
    }
    
    // Listen for visibility change (user returning from Apple Pay)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && paymentLoading) {
        // User came back - might have completed Apple Pay
        // Check if there's a stored session to verify
        const storedSession = sessionStorage.getItem('pending_checkout_session');
        if (storedSession) {
          setCheckingPayment(true);
          pollPaymentStatus(storedSession);
          sessionStorage.removeItem('pending_checkout_session');
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [searchParams, paymentLoading]);

  // Poll for payment status
  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please try again.');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/payments/status/${sid}`);
      
      if (response.data.is_paid) {
        setPaymentVerified(true);
        setCheckingPayment(false);
        setShowPaymentBanner(true);
        // Auto-hide payment banner after 5 seconds
        setTimeout(() => setShowPaymentBanner(false), 5000);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      } else if (response.data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setCheckingPayment(false);
      toast.error('Failed to verify payment. Please contact support.');
    }
  };

  // Initiate payment
  const handlePayment = async () => {
    setPaymentLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/payments/checkout/emergency-card`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        // Store session ID in case Apple Pay doesn't redirect properly
        sessionStorage.setItem('pending_checkout_session', response.data.session_id);
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  // Format DOB as user types (MM/DD/YYYY) - smart formatting
  const handleDOBChange = (e) => {
    let value = e.target.value;
    let prevValue = formData.date_of_birth || '';
    
    // Detect if user typed a slash after single digit (like "1/")
    // This means they want single-digit month
    if (value.length === 2 && value[1] === '/' && prevValue.length === 1) {
      value = '0' + value;
    }
    
    // Remove all non-digits
    let digits = value.replace(/\D/g, '');
    
    // Smart month formatting
    if (digits.length >= 1) {
      const firstDigit = parseInt(digits[0]);
      
      // If first digit > 1, it can only be single digit month (2-9), prepend 0
      if (firstDigit > 1) {
        digits = '0' + digits;
      }
      // If typing "1" followed by digit > 2, treat "1" as the month
      // This handles: 13, 14, 15... -> 01/3, 01/4, 01/5...
      else if (firstDigit === 1 && digits.length >= 2) {
        const secondDigit = parseInt(digits[1]);
        if (secondDigit > 2) {
          digits = '0' + digits;
        }
      }
    }
    
    // Format with slashes (MM/DD/YYYY)
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.substring(0, 2);
    }
    if (digits.length > 2) {
      formatted += '/' + digits.substring(2, 4);
    }
    if (digits.length > 4) {
      formatted += '/' + digits.substring(4, 8);
    }
    
    setFormData({ ...formData, date_of_birth: formatted });
  };

  // Severity colors and labels
  const severityConfig = {
    severe: { color: 'bg-[#D92B2B]', label: 'Severe', description: 'Life-threatening' },
    moderate: { color: 'bg-orange-500', label: 'Moderate', description: 'Significant reaction' },
    mild: { color: 'bg-yellow-500', label: 'Mild', description: 'Minor reaction' }
  };

  const addAllergyWithSeverity = (severity) => {
    if (pendingAllergy) {
      const newAllergyObj = { name: pendingAllergy, severity };
      setFormData({
        ...formData,
        allergies: [...formData.allergies, newAllergyObj].sort((a, b) => {
          // Sort by severity: severe first, then moderate, then mild
          const order = { severe: 0, moderate: 1, mild: 2 };
          return order[a.severity] - order[b.severity];
        })
      });
      setPendingAllergy(null);
      setNewAllergy('');
    }
  };

  // Generate QR code locally with embedded data (Own Your Data approach)
  const generateLocalQRCode = async () => {
    // Compress data for QR code
    const cardData = {
      n: formData.full_name,
      d: formData.date_of_birth,
      b: formData.blood_group,
      g: formData.gender,
      a: formData.allergies.map(a => ({ n: a.name, s: a.severity[0] })), // s: m/s/M for mild/severe/moderate
      c: formData.critical_conditions,
      m: formData.current_medications,
      su: formData.previous_surgeries.filter(s => s.name).map(s => ({ n: s.name, y: s.year })), // surgeries
      e: formData.emergency_contacts.filter(c => c.name && c.phone).map(c => ({
        n: c.name,
        r: c.relationship,
        p: c.phone,
        i: c.is_primary ? 1 : 0
      })),
      o: formData.organ_donor ? 1 : 0,
      h: formData.preferred_hospital,
      dn: formData.doctor_name,
      dp: formData.doctor_phone,
      s: formData.special_instructions,
      th: formData.color_theme, // Theme
      t: Date.now() // timestamp
    };
    
    // Encode to base64
    const jsonStr = JSON.stringify(cardData);
    const base64Data = btoa(unescape(encodeURIComponent(jsonStr)));
    
    // Create QR code URL - use production domain or current origin for dev
    const productionDomain = 'https://myemergencycard.app';
    const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent');
    const baseUrl = isProduction ? productionDomain : window.location.origin;
    // Use correct path based on current context
    const viewPath = window.location.pathname.includes('emergency-app') ? '/emergency-app/view' : '/view';
    const viewerUrl = `${baseUrl}${viewPath}?d=${base64Data}`;
    
    return { viewerUrl, base64Data };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.blood_group) {
      toast.error('Please fill Name and Blood Group');
      return;
    }
    
    setSaving(true);
    try {
      // Generate QR data locally - no server call needed!
      const { viewerUrl, base64Data } = await generateLocalQRCode();
      
      // Generate QR code image using a canvas (client-side)
      const QRCode = (await import('qrcode')).default;
      const qrDataUrl = await QRCode.toDataURL(viewerUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#D32F2F',
          light: '#FFFFFF'
        }
      });
      
      // Extract base64 from data URL
      const qrBase64 = qrDataUrl.split(',')[1];
      
      setSavedData({
        qr_code: qrBase64,
        emergency_url: viewerUrl,
        data_encoded: base64Data,
        created_at: new Date().toISOString()
      });
      setShowSuccessDialog(true);
      toast.success('Emergency Card created!');
    } catch (error) {
      console.error('QR generation error:', error);
      toast.error('Failed to create card. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addEmergencyContact = () => {
    if (formData.emergency_contacts.length < 3) {
      setFormData({
        ...formData,
        emergency_contacts: [...formData.emergency_contacts, { name: '', relationship: '', phone: '', is_primary: false }]
      });
    }
  };

  const removeEmergencyContact = (index) => {
    const updated = formData.emergency_contacts.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some(c => c.is_primary)) {
      updated[0].is_primary = true;
    }
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const updateContact = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'is_primary' && value) {
      updated.forEach((c, i) => {
        if (i !== index) c.is_primary = false;
      });
    }
    
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addItem = (field, value) => {
    if (value && !formData[field].includes(value)) {
      setFormData({ ...formData, [field]: [...formData[field], value] });
    }
  };

  const removeItem = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  const downloadQRCode = () => {
    if (!savedData?.qr_code) {
      toast.error('QR code not available. Please create your card first.');
      return;
    }
    
    try {
      // Create a canvas to generate a nice card image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Card dimensions (wallet card size ratio)
      const cardWidth = 400;
      const cardHeight = 500;
      const padding = 30;
      const qrSize = 280;
      
      canvas.width = cardWidth;
      canvas.height = cardHeight;
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, cardHeight);
      gradient.addColorStop(0, '#fecaca');
      gradient.addColorStop(1, '#fee2e2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, cardWidth, cardHeight);
      
      // White card area with rounded corners
      const cardX = padding;
      const cardY = padding;
      const innerWidth = cardWidth - (padding * 2);
      const innerHeight = cardHeight - (padding * 2);
      const radius = 20;
      
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.moveTo(cardX + radius, cardY);
      ctx.lineTo(cardX + innerWidth - radius, cardY);
      ctx.quadraticCurveTo(cardX + innerWidth, cardY, cardX + innerWidth, cardY + radius);
      ctx.lineTo(cardX + innerWidth, cardY + innerHeight - radius);
      ctx.quadraticCurveTo(cardX + innerWidth, cardY + innerHeight, cardX + innerWidth - radius, cardY + innerHeight);
      ctx.lineTo(cardX + radius, cardY + innerHeight);
      ctx.quadraticCurveTo(cardX, cardY + innerHeight, cardX, cardY + innerHeight - radius);
      ctx.lineTo(cardX, cardY + radius);
      ctx.quadraticCurveTo(cardX, cardY, cardX + radius, cardY);
      ctx.closePath();
      ctx.fill();
      
      // Red border
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      // Load QR code image
      const qrImage = new Image();
      
      qrImage.onload = () => {
        // Draw QR code centered
        const qrX = (cardWidth - qrSize) / 2;
        const qrY = padding + 40;
        ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
        
        // Add "EMERGENCY CARD" text
        ctx.fillStyle = '#dc2626';
        ctx.font = 'bold 24px -apple-system, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('EMERGENCY CARD', cardWidth / 2, qrY + qrSize + 40);
        
        // Add "Scan for medical info" subtitle
        ctx.fillStyle = '#666666';
        ctx.font = '14px -apple-system, system-ui, sans-serif';
        ctx.fillText('Scan for medical info', cardWidth / 2, qrY + qrSize + 65);
        
        // Add name if available
        if (formData.full_name) {
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 16px -apple-system, system-ui, sans-serif';
          ctx.fillText(formData.full_name, cardWidth / 2, qrY + qrSize + 95);
        }
        
        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/png');
        
        // Check if iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (isIOS) {
          // For iOS - open image in new tab for long-press save
          // Using location.href works better than window.open for iOS Safari
          const link = document.createElement('a');
          link.href = dataUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          // Create a blob URL which sometimes works better on iOS
          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              // Try to trigger native share if available
              if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], 'Emergency_Card.png', { type: 'image/png' })] })) {
                navigator.share({
                  files: [new File([blob], 'Emergency_Card.png', { type: 'image/png' })],
                  title: 'My Emergency Card',
                }).then(() => {
                  toast.success('Shared successfully!');
                }).catch(() => {
                  // Fallback - open in new window
                  window.open(dataUrl, '_blank');
                  toast.info('Long-press the image to save it');
                });
              } else {
                // Fallback - open in new window  
                window.open(dataUrl, '_blank');
                toast.info('Long-press the image to save it');
              }
              URL.revokeObjectURL(blobUrl);
            }
          }, 'image/png');
        } else {
          // Standard download for other browsers
          const timestamp = Date.now().toString(36);
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `Emergency_Card_${timestamp}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Card saved!');
        }
      };
      
      qrImage.onerror = () => {
        console.error('Failed to load QR image');
        // Fallback - just download the raw QR code
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${savedData.qr_code}`;
        link.download = 'Emergency_QR.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('QR code saved!');
      };
      
      qrImage.src = `data:image/png;base64,${savedData.qr_code}`;
    } catch (error) {
      console.error('Download error:', error);
      // Ultimate fallback - just save the raw QR
      try {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${savedData.qr_code}`;
        link.download = 'Emergency_QR.png';
        link.click();
        toast.success('QR saved!');
      } catch (e) {
        toast.error('Unable to save. Please screenshot the QR code.');
      }
    }
  };

  const printCard = () => {
    // Print is not available in "Own Your Data" mode since data isn't on server
    toast.info('Save the QR code image to print it');
  };

  // Share QR code using native share (works on iOS)
  const shareQRCode = async () => {
    if (savedData?.qr_code && navigator.share) {
      try {
        // Convert base64 to blob
        const timestamp = Date.now().toString(36);
        const response = await fetch(`data:image/png;base64,${savedData.qr_code}`);
        const blob = await response.blob();
        const file = new File([blob], `Emergency_Card_${timestamp}.png`, { type: 'image/png' });
        
        await navigator.share({
          title: 'Emergency Card QR',
          text: 'My emergency medical information QR code',
          files: [file]
        });
      } catch (err) {
        // Fallback to URL share
        if (navigator.share) {
          await navigator.share({
            title: 'Emergency Card',
            text: 'My emergency medical information',
            url: savedData.emergency_url
          });
        }
      }
    } else if (savedData?.emergency_url) {
      // Copy URL as fallback
      navigator.clipboard.writeText(savedData.emergency_url);
      toast.success('Link copied!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      {/* Header */}
      <header className="bg-[#D92B2B] text-white px-4 py-3 sticky top-0 z-40 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => {
                const basePath = window.location.pathname.includes('emergency-app') ? '/emergency-app' : '/';
                navigate(basePath);
              }}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">My EmergencyCard App</h1>
              <p className="text-xs text-red-200">Medical QR Card</p>
            </div>
          </div>
          <img src="/logo.png" alt="Logo" className="h-8 w-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 pb-24">
        {/* Payment Verification Loading */}
        {checkingPayment && (
          <Card className="mb-5 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                <div>
                  <h2 className="font-bold text-blue-700 text-sm">Verifying Payment...</h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Please wait while we confirm your payment.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Required Section */}
        {!paymentVerified && !checkingPayment && (
          <Card className="mb-5 border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-5">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <CreditCard className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="font-bold text-green-800 text-lg mb-2">Complete Payment to Continue</h2>
                <p className="text-sm text-slate-600 mb-4">
                  One-time payment of <strong className="text-green-700">$1 USD</strong> for your Emergency Card.
                  <br />
                  <span className="text-xs text-slate-500">Supports Apple Pay, Google Pay, and all major cards.</span>
                </p>
                
                <Button 
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className="w-full max-w-xs h-12 bg-[#D92B2B] hover:bg-[#C02424] text-white shadow-lg"
                  data-testid="pay-now-btn"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Redirecting to Payment...
                    </>
                  ) : (
                    <>
                      <Lock className="h-5 w-5 mr-2" />
                      Pay $1 - Secure Checkout
                    </>
                  )}
                </Button>
                
                <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Secure
                  </span>
                  <span>•</span>
                  <span>Powered by Stripe</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Success Banner - Auto-hides after 5 seconds */}
        {paymentVerified && showPaymentBanner && (
          <Card className="mb-5 border-green-200 bg-green-50 animate-in fade-in duration-300">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-green-700 text-sm">Payment Confirmed!</h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Fill out your details below to create your Emergency Card.
                  </p>
                </div>
                <button 
                  onClick={() => setShowPaymentBanner(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ×
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form - Only show after payment */}
        {paymentVerified && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-[#D92B2B]" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div>
                <Label className="text-sm">Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="As on ID card"
                  required
                  className="h-10 mt-1"
                />
              </div>
              
              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-2">
                  <Label className="text-sm">Date of Birth</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formData.date_of_birth}
                    onChange={handleDOBChange}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    className="h-10 mt-1 text-sm tracking-wider"
                  />
                </div>
                <div className="col-span-3">
                  <Label className="text-sm">Blood Group *</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {BLOOD_GROUPS.map(bg => (
                      <button
                        key={bg}
                        type="button"
                        onClick={() => setFormData({ ...formData, blood_group: bg })}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                          formData.blood_group === bg 
                            ? 'bg-[#D92B2B] text-white border-[#D92B2B]' 
                            : 'bg-white text-slate-700 border-slate-300 hover:border-red-400'
                        }`}
                      >
                        {bg}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm">Gender</Label>
                <div className="flex gap-2 mt-1">
                  {['Male', 'Female', 'Other'].map(g => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setFormData({ ...formData, gender: g.toLowerCase() })}
                      className={`px-4 py-1.5 rounded-md text-sm border transition-all ${
                        formData.gender === g.toLowerCase()
                          ? 'bg-slate-700 text-white border-slate-700'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#D92B2B]" />
                Emergency Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {formData.emergency_contacts.map((contact, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      Contact {index + 1}
                      {contact.is_primary && (
                        <Badge className="ml-2 bg-[#E63333] text-xs">Primary</Badge>
                      )}
                    </span>
                    {formData.emergency_contacts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmergencyContact(index)}
                        className="text-[#E63333] h-7 w-7 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <Input
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) => updateContact(index, 'name', e.target.value)}
                    className="h-9"
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">Relationship</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {RELATIONSHIPS.slice(0, 4).map(rel => (
                          <button
                            key={rel}
                            type="button"
                            onClick={() => updateContact(index, 'relationship', rel.toLowerCase())}
                            className={`px-2 py-1 rounded text-xs border transition-all ${
                              contact.relationship === rel.toLowerCase()
                                ? 'bg-slate-700 text-white border-slate-700'
                                : 'bg-white text-slate-600 border-slate-300'
                            }`}
                          >
                            {rel}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Phone</Label>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        placeholder="Phone number"
                        value={contact.phone}
                        onChange={(e) => {
                          // Only allow numbers, +, -, (, ), and spaces for phone formatting
                          const val = e.target.value.replace(/[^0-9+\-() ]/g, '');
                          updateContact(index, 'phone', val);
                        }}
                        className="h-8 mt-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {formData.emergency_contacts.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addEmergencyContact}
                  className="w-full border-dashed h-9 text-sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Allergies with Severity Levels */}
          <Card className="border-red-200">
            <CardHeader className="pb-2 pt-4 bg-red-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2 text-[#C02424]">
                <AlertTriangle className="h-4 w-4" />
                Allergies (CRITICAL)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 space-y-3">
              {/* Display allergies sorted by severity */}
              <div className="flex flex-wrap gap-1.5">
                {formData.allergies.map((allergy, index) => (
                  <Badge 
                    key={index} 
                    className={`${severityConfig[allergy.severity]?.color || 'bg-[#D92B2B]'} text-white flex items-center gap-1 text-xs`}
                  >
                    {allergy.severity === 'severe' && '⚠️ '}
                    {allergy.name}
                    <button type="button" onClick={() => removeItem('allergies', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              {/* Severity Selection Modal */}
              {pendingAllergy && (
                <div className="p-3 bg-slate-100 rounded-lg border-2 border-slate-300">
                  <p className="text-sm font-medium mb-2">
                    Select severity for: <strong>{pendingAllergy}</strong>
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => addAllergyWithSeverity('severe')}
                      className="flex-1 py-2 px-3 bg-[#D92B2B] text-white rounded-md text-xs font-medium hover:bg-[#C02424]"
                    >
                      🔴 Severe
                      <span className="block text-red-200 text-[10px]">Life-threatening</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => addAllergyWithSeverity('moderate')}
                      className="flex-1 py-2 px-3 bg-orange-500 text-white rounded-md text-xs font-medium hover:bg-orange-600"
                    >
                      🟠 Moderate
                      <span className="block text-orange-200 text-[10px]">Significant</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => addAllergyWithSeverity('mild')}
                      className="flex-1 py-2 px-3 bg-yellow-500 text-white rounded-md text-xs font-medium hover:bg-yellow-600"
                    >
                      🟡 Mild
                      <span className="block text-yellow-200 text-[10px]">Minor</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingAllergy(null)}
                    className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
              
              {/* Autocomplete Input */}
              {!pendingAllergy && (
                <>
                  <AutocompleteInput
                    value={newAllergy}
                    onChange={setNewAllergy}
                    onAdd={(val) => setPendingAllergy(val)}
                    suggestions={allergySuggestions.filter(a => !formData.allergies.some(al => al.name === a))}
                    placeholder="Type 3+ letters to search allergies..."
                  />
                  
                  <div className="flex flex-wrap gap-1">
                    {allergySuggestions.filter(a => !formData.allergies.some(al => al.name === a)).slice(0, 4).map(allergy => (
                      <Button
                        key={allergy}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-6 px-2"
                        onClick={() => setPendingAllergy(allergy)}
                      >
                        + {allergy}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              
              {/* Legend */}
              <div className="flex gap-3 text-[10px] text-slate-500 pt-1">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#D92B2B] rounded-full"></span> Severe</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-full"></span> Moderate</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full"></span> Mild</span>
              </div>
            </CardContent>
          </Card>

          {/* Medical Conditions with Autocomplete */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Heart className="h-4 w-4 text-orange-600" />
                Medical Conditions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="flex flex-wrap gap-1.5">
                {formData.critical_conditions.map((condition, index) => (
                  <Badge key={index} className="bg-orange-500 flex items-center gap-1 text-xs">
                    {condition}
                    <button type="button" onClick={() => removeItem('critical_conditions', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <AutocompleteInput
                value={newCondition}
                onChange={setNewCondition}
                onAdd={(val) => addItem('critical_conditions', val)}
                suggestions={COMMON_CONDITIONS.filter(c => !formData.critical_conditions.includes(c))}
                placeholder="Type 3+ letters to search conditions..."
              />
              
              <div className="flex flex-wrap gap-1">
                {COMMON_CONDITIONS.filter(c => !formData.critical_conditions.includes(c)).slice(0, 4).map(condition => (
                  <Button
                    key={condition}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6 px-2"
                    onClick={() => addItem('critical_conditions', condition)}
                  >
                    + {condition}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Current Medications with Autocomplete */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Pill className="h-4 w-4 text-blue-600" />
                Current Medications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="flex flex-wrap gap-1.5">
                {formData.current_medications.map((med, index) => (
                  <Badge key={index} className="bg-blue-500 flex items-center gap-1 text-xs">
                    {med}
                    <button type="button" onClick={() => removeItem('current_medications', index)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <AutocompleteInput
                value={newMedication}
                onChange={setNewMedication}
                onAdd={(val) => addItem('current_medications', val)}
                suggestions={medicationSuggestions.filter(m => !formData.current_medications.includes(m))}
                placeholder="Type 3+ letters to search medications..."
              />
              
              <div className="flex flex-wrap gap-1">
                {medicationSuggestions.filter(m => !formData.current_medications.includes(m)).slice(0, 4).map(med => (
                  <Button
                    key={med}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6 px-2"
                    onClick={() => addItem('current_medications', med)}
                  >
                    + {med}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Previous Surgeries */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Hospital className="h-4 w-4 text-purple-600" />
                Previous Surgeries
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {formData.previous_surgeries.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.previous_surgeries.map((surgery, index) => (
                    <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1 bg-purple-100 text-purple-800">
                      {surgery.name} {surgery.year && `(${surgery.year})`}
                      <button
                        type="button"
                        onClick={() => {
                          const updated = formData.previous_surgeries.filter((_, i) => i !== index);
                          setFormData({ ...formData, previous_surgeries: updated });
                        }}
                        className="ml-1 hover:bg-purple-200 rounded p-0.5"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <Input
                  value={newSurgery}
                  onChange={(e) => setNewSurgery(e.target.value)}
                  placeholder="Surgery name (e.g., Knee Replacement)"
                  className="h-9 flex-1"
                />
                <Input
                  value={newSurgeryYear}
                  onChange={(e) => {
                    // Only allow numbers
                    const val = e.target.value.replace(/\D/g, '');
                    setNewSurgeryYear(val);
                  }}
                  placeholder="Year"
                  className="h-9 w-20"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    if (newSurgery.trim()) {
                      setFormData({
                        ...formData,
                        previous_surgeries: [...formData.previous_surgeries, { name: newSurgery.trim(), year: newSurgeryYear.trim() }]
                      });
                      setNewSurgery('');
                      setNewSurgeryYear('');
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-1">
                {['Heart Bypass', 'Pacemaker', 'Hip Replacement', 'Knee Replacement', 'Appendectomy', 'C-Section'].filter(s => 
                  !formData.previous_surgeries.some(ps => ps.name === s)
                ).slice(0, 4).map(surgery => (
                  <Button
                    key={surgery}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6 px-2"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        previous_surgeries: [...formData.previous_surgeries, { name: surgery, year: '' }]
                      });
                    }}
                  >
                    + {surgery}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Hospital className="h-4 w-4 text-green-600" />
                Additional Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Doctor Name</Label>
                  <Input
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                    placeholder="Dr."
                    className="h-9 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Doctor Phone</Label>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={formData.doctor_phone}
                    onChange={(e) => {
                      // Only allow numbers, +, -, (, ), and spaces for phone formatting
                      const val = e.target.value.replace(/[^0-9+\-() ]/g, '');
                      setFormData({ ...formData, doctor_phone: val });
                    }}
                    placeholder="Phone"
                    className="h-9 mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm">Preferred Hospital</Label>
                <Input
                  value={formData.preferred_hospital}
                  onChange={(e) => setFormData({ ...formData, preferred_hospital: e.target.value })}
                  placeholder="Hospital name"
                  className="h-9 mt-1"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <Label className="text-sm">Organ Donor</Label>
                  <p className="text-xs text-slate-500">Registered donor</p>
                </div>
                <Switch
                  checked={formData.organ_donor}
                  onCheckedChange={(checked) => setFormData({ ...formData, organ_donor: checked })}
                />
              </div>
              
              <div>
                <Label className="text-sm">Critical Alerts (select if applicable)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {['Pregnant', 'On Dialysis', 'Organ Transplant', 'Immunocompromised'].map((condition) => (
                    <Button
                      key={condition}
                      type="button"
                      variant={formData.critical_conditions.includes(condition) ? 'default' : 'outline'}
                      size="sm"
                      className={`text-xs ${formData.critical_conditions.includes(condition) ? 'bg-[#D92B2B] hover:bg-[#C02424]' : ''}`}
                      onClick={() => {
                        if (formData.critical_conditions.includes(condition)) {
                          setFormData({
                            ...formData,
                            critical_conditions: formData.critical_conditions.filter(c => c !== condition)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            critical_conditions: [...formData.critical_conditions, condition]
                          });
                        }
                      }}
                    >
                      {condition}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-sm">Special Instructions</Label>
                <Textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  placeholder="DNR wishes, religious preferences, etc."
                  rows={2}
                  className="mt-1 text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Card Color Theme - Compact */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-slate-700">Card Style</span>
            </div>
            <div className="flex gap-2">
              {COLOR_THEMES.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, color_theme: theme.id })}
                  className={`w-8 h-8 rounded-full ${theme.preview} transition-all ${
                    formData.color_theme === theme.id 
                      ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' 
                      : 'hover:scale-105'
                  }`}
                  title={theme.name}
                >
                  {formData.color_theme === theme.id && (
                    <CheckCircle className="h-4 w-4 text-white mx-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-[#D92B2B] hover:bg-[#C02424] h-12 text-base"
            disabled={saving}
            data-testid="create-card-btn"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              <QrCode className="h-5 w-5 mr-2" />
            )}
            Create Emergency QR Card
          </Button>
        </form>
        )}
      </main>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              Card Created!
            </DialogTitle>
            <DialogDescription className="text-sm">
              Save this QR to your phone. In emergencies, show it to medical staff.
            </DialogDescription>
          </DialogHeader>
          
          {savedData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-lg shadow-lg border-2 border-red-200">
                  <img 
                    src={`data:image/png;base64,${savedData.qr_code}`} 
                    alt="Emergency QR Code" 
                    className="w-40 h-40"
                  />
                </div>
              </div>
              
              {/* Own Your Data Badge */}
              <div className="flex items-center justify-center gap-2 text-green-700 text-sm">
                <Lock className="h-4 w-4" />
                <span>Your data is stored in this QR only - not on our servers</span>
              </div>
              
              {/* Save to Phone - Primary Action */}
              <Button 
                onClick={downloadQRCode}
                className="w-full bg-[#D92B2B] hover:bg-[#C02424] h-11"
                data-testid="save-qr-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Save QR to Phone
              </Button>
              
              <p className="text-xs text-center text-slate-500">
                Set as lock screen wallpaper or print for your wallet
              </p>
              
              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Important: Save this QR code!</p>
                    <p className="text-xs text-amber-700 mt-1">
                      We don't store your data. If you lose this QR code, you'll need to create a new one.
                      Back it up to your cloud photos or save in multiple places.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Preview Button */}
              <Button 
                onClick={() => window.open(savedData.emergency_url, '_blank')}
                variant="outline"
                className="w-full"
                size="sm"
                data-testid="preview-card-btn"
              >
                Preview Card (Test Scan)
              </Button>
              
              {/* Edit Link */}
              <button
                onClick={() => {
                  // Extract the encoded data from the URL
                  const url = new URL(savedData.emergency_url);
                  const encodedData = url.searchParams.get('d');
                  if (encodedData) {
                    setShowSuccessDialog(false);
                    navigate(`/create?edit=${encodedData}`);
                  }
                }}
                className="text-sm text-slate-500 hover:text-[#D92B2B] underline"
                data-testid="edit-card-btn"
              >
                Made a mistake? Edit your card
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateEmergencyCardPage;
